import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { DATA_SET_UPLOAD_FILE, SELECTED_ITEMS } from 'constants/formFields';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { PRESIGNED_FILE_TYPES } from 'constants/interfaces';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import useDataSetsUploadForm from 'hooks/useDataSetsUploadForm';
import useErrorMessage from 'hooks/useErrorMessage';
import useFileStorageUpload from 'hooks/useFileStorageUpload';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useGetMaterialityStakeholdersListStatusQuery, useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import {
  useCheckGroupQuestionsMutation,
  useGetSingleMAInitiateQuery, useGetStakeholdersGroupsQuestionsListQuery,
  useImportQuestionsExcelMutation, useLazyDownloadQuestionsQuery, useLazyGetStakeholdersQuestionsListQuery,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import useQuestionForm from './hooks/useQuestionForm';
import MaterialityConfigInvestorsCustomersEvidenceView from './MaterialityConfigInvestorsCustomersEvidenceView';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IQuestionErrorList from 'components/QuestionErrorModal/interfaces/IQuestionErrorList';
import type { TPresignedFileTypes } from 'constants/interfaces';
import type IQuestionsItem from 'store/slices/materialAssessment/interfaces/IQuestionsItem';
import type IStakeholderGroupsQuestionsItem from 'store/slices/materialAssessment/interfaces/IStakeholderGroupsQuestionsItem';

function MaterialityConfigInvestorsCustomersEvidence() {
  const navigate = useNavigate();
  const { recordId = '', vcmId = 0 } = useParams();
  const { control: uploadControl, watch, reset } = useDataSetsUploadForm();
  const [openModal, setOpenModal] = useState('');
  const dataFile = watch(DATA_SET_UPLOAD_FILE);
  const [items, setItems] = useState<IStakeholderGroupsQuestionsItem[]>([]);
  const [questions, setQuestions] = useState<IQuestionsItem[]>([]);
  const [expandedRowId, setExpandedRowId] = useState<number>(-1);
  const [offset, setOffset] = useState(0);
  const [loadingImport, setLoadingImport] = useState(false);
  const [observeStatus, setObserveStatus] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [options, setOptions] = useState<IOption[]>([]);
  const [shouldShowNotification, setShouldShowNotification] = useState(false);
  const [questionErrorModal, setQuestionErrorModal] = useState(false);
  const [questionErrorList, setQuestionErrorList] = useState<IQuestionErrorList>();
  const [errorLog, setErrorLog] = useState<string>('');

  const [checkGroupQuestions] = useCheckGroupQuestionsMutation();
  const { uploadToStorage, isLoading } = useFileStorageUpload();
  const [importQuestionsExcel, { isLoading: isLoadingImport }] = useImportQuestionsExcelMutation();
  const [updateSection] = useUpdateSectionMAMutation();

  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });

  const [getStakeholdersQuestionsList] = useLazyGetStakeholdersQuestionsListQuery();

  const { data: sectionsData, refetch: refetchSections } = useGetMaterialityStakeholdersListStatusQuery({
    maId: recordId,
    type: SECTION_TYPE.TOPIC_1_REQUESTS,
    vcmId: Number(vcmId),
  }, {
    skip: Number(vcmId) === 0,
  });
  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();

  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  useEffect(() => {
    if (sectionsData && sectionsData.data) {
      setCurrentStatus(sectionsData.data.status);
    }
  }, [sectionsData]);

  const [downloadQuestions] = useLazyDownloadQuestionsQuery();
  const {
    data: groupsList,
    isFetching: isLoadingGroups,
    error: fetchError,
    refetch: refetchGroups,
  } = useGetStakeholdersGroupsQuestionsListQuery({
    maId: recordId,
    offset,
    valueChainMapId: Number(vcmId),
  }, {
    skip: Number(vcmId) === 0,
  });

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (currentStatus === STATUS_SECTIONS.INTERNAL_PROCESSING) {
      setObserveStatus(true);
      intervalId = setInterval(() => {
        refetchSections();
      }, 5000);
    } else if (observeStatus && currentStatus !== STATUS_SECTIONS.INTERNAL_PROCESSING) {
      setObserveStatus(false);
      if (loadingImport) {
        setShouldShowNotification(true);
        refetchGroups();
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentStatus, observeStatus, loadingImport, refetchSections, refetchGroups]);

  useEffect(() => {
    if (shouldShowNotification
      && !observeStatus
      && !isLoadingGroups
      && groupsList
      && loadingImport) {
      setLoadingImport(false);
    }
  }, [shouldShowNotification, observeStatus, isLoadingGroups, groupsList, loadingImport]);

  useEffect(() => {
    if (shouldShowNotification
      && !observeStatus
      && !isLoadingGroups
      && groupsList
      && !loadingImport) {
      setShouldShowNotification(false);
      toast.success(i18n.t('notifications.uploadQuestionsSuccess'));
    }
  }, [shouldShowNotification, observeStatus, isLoadingGroups, groupsList, loadingImport]);

  const errorMessage = useErrorMessage(fetchError);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  const hasMore = groupsList?.data?.meta.hasNext;

  const loadMore = () => {
    if (groupsList?.data.meta.hasNext) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (groupsList && groupsList?.data?.items.length > 0) {
      setItems((prev) => {
        if (offset === 0) {
          return groupsList.data.items.map((item) => ({ ...item, custom: true }));
        }
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = groupsList?.data?.items
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({ ...item, custom: true }));
        return [...prev, ...newItems];
      });
    }
  }, [groupsList, offset]);

  useEffect(() => {
    if (expandedRowId !== -1) {
      getStakeholdersQuestionsList({ maId: recordId, id: expandedRowId, vcmId: Number(vcmId) }).then((response) => {
        const responseData = response.data?.data.items;
        if (responseData) {
          setQuestions(responseData);
        }
      });
    }
  }, [expandedRowId, getStakeholdersQuestionsList, recordId, vcmId]);

  const {
    control: questionControl,
    setValue,
  } = useQuestionForm();

  useEffect(() => {
    if (dataFile) {
      reset({
        [DATA_SET_UPLOAD_FILE]: '',
      });
    }
  }, [dataFile, reset]);

  const goBack = () => {
    if (observeStatus) {
      setObserveStatus(false);
    }
    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const onSave = () => {
    if (isViewOnly) {
      navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
      return;
    }
    updateSection({
      maId: recordId,
      type: SECTION_TYPE.TOPIC_1_REQUESTS,
      valueChainMapId: Number(vcmId),
      data: {
        isActive: true,
        status: items.length > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.EMPTY,
      },
    });
    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const onDownloadExample = () => {
    downloadQuestions({ maId: recordId, vcmId: Number(vcmId) });
  };

  const onSelectItem = (id: number, isSelected: boolean) => {
    if (access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY) {
      return;
    }
    checkGroupQuestions({
      maId: recordId, groupIds: [id], isActive: isSelected, vcmId: Number(vcmId),
    }).then(() => {
      toast.success(i18n.t('notifications.selectedQuestionsGroup'));
    }).catch(() => {
      toast.error(i18n.t('notifications.errorSelectedQuestionsGroup'));
    });
  };

  const onOpenModal = (id: string) => {
    if (access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY) {
      return;
    }
    setOpenModal(id);
  };

  const onCloseModal = () => {
    setOpenModal('');
    setQuestionErrorModal(false);
    setErrorLog('');
    setQuestionErrorList(undefined);
  };

  const handleQuestionGroupCreated = useCallback(() => {
    setOffset(0);
    refetchGroups();
  }, [refetchGroups]);

  const uploadFile = useCallback((file: File | string, fileType: TPresignedFileTypes) => {
    if (typeof file === 'object') {
      return uploadToStorage(file, fileType)
        .then((presignedData) => presignedData.fields.Url)
        .catch(() => '');
    }
    return Promise.resolve(file);
  }, [uploadToStorage]);

  useEffect(() => {
    if (dataFile && !isLoading && recordId && access !== TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY) {
      uploadFile(dataFile, PRESIGNED_FILE_TYPES.materialityAssessmentImport).then((url) => {
        importQuestionsExcel({
          maId: recordId,
          url,
          valueChainMapId: Number(vcmId),
        }).then((response) => {
          setLoadingImport(true);
          if ('error' in response && response.error && 'data' in response.error) {
            const errorData = response.error.data as { message?: { rowErrors?: IQuestionErrorList } } | undefined;
            if (Array.isArray(errorData?.message)) {
              setErrorLog(errorData?.message[0][0].error);
            } else {
              setQuestionErrorList(errorData?.message?.rowErrors);
            }
            setQuestionErrorModal(true);
            setObserveStatus(false);
            setLoadingImport(false);
            toast.error(i18n.t('notifications.uploadQuestionsError2'));
          } else {
            setOpenModal('');
            setOffset(0);
            refetchSections();
          }
        });
      }).catch(() => {
        toast.error(i18n.t('notifications.uploadQuestionsError'));
      });
      reset({
        [DATA_SET_UPLOAD_FILE]: '',
      });
    }
  }, [dataFile, uploadFile, reset, isLoading, importQuestionsExcel, recordId,
    refetchSections, vcmId, access]);

  useEffect(() => {
    if (items.length > 0) {
      setValue(SELECTED_ITEMS, items.filter((item) => item.isActive).map((item) => item.id));
    }
  }, [items, setValue]);

  useEffect(() => {
    if (singleMA) {
      const vcList = singleMA?.data?.valueChainMaps?.map(
        (itemVC) => ({ label: itemVC?.name, value: `${itemVC.id}` }),
      ) || [];
      setOptions([{ label: 'All', value: '' }, ...vcList]);
    }
  }, [singleMA]);

  const isSaveDisabled = isLoadingImport || loadingImport || observeStatus || isUserLoading;

  return (
    <MaterialityConfigInvestorsCustomersEvidenceView
      goBack={goBack}
      onSave={onSave}
      onDownloadExample={onDownloadExample}
      uploadControl={uploadControl}
      isEmptyList={items.length === 0}
      control={questionControl}
      isLineMark={false}
      items={items}
      onOpenModal={onOpenModal}
      openModal={openModal}
      onCloseModal={onCloseModal}
      hasMore={hasMore}
      loadMore={loadMore}
      expandedRowId={expandedRowId}
      setExpandedRowId={setExpandedRowId}
      questions={questions}
      onSelectItem={onSelectItem}
      recordId={recordId}
      loading={isLoadingImport || loadingImport || observeStatus || isLoadingSingleMA}
      loadReq={isLoading || isLoadingGroups}
      isSaveDisabled={isSaveDisabled}
      setOpenModal={setOpenModal}
      vcOptions={options}
      vcmId={Number(vcmId)}
      questionErrorModal={questionErrorModal}
      questionErrorList={questionErrorList}
      errorLog={errorLog}
      onQuestionGroupCreated={handleQuestionGroupCreated}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialityConfigInvestorsCustomersEvidence;
